function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

function handleEventListeners(events = [], type = "add") {
  const eventList = Array.isArray(events) ? events : [events];

  for (const event of eventList) {
    if (type === "add") {
      event.el.addEventListener(event.type, event.callback, event.capture || false);
    } else {
      event.el.removeEventListener(event.type, event.callback, event.capture || false);
    }
  }
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute("form", `${element.form.getAttribute("id")}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", summary.parentNode.hasAttribute("open"));

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open"),
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*",
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this),
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === "minus") {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle("disabled", parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options["hideElement"] || province_domid);

  Shopify.addListener(this.countryEl, "change", Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)",
    ).forEach((button) => button.addEventListener("click", this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary"))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector("button"));
      summaryElement.nextElementSibling.removeEventListener("transitionend", addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener("transitionend", addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((submenu) => {
      submenu.classList.remove("submenu-open");
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement.querySelector("summary").setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary"),
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains("header-wrapper--border-bottom")
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
      );
    document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false),
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (event.pointerType === "mouse" && !event.target.closest("deferred-media, product-model"))
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace("shopify-section-", "");
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, "text/html");
            const sourceQty = html.querySelector(".quick-order-list-container").parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`),
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(this.querySelector("template").content.firstElementChild.cloneNode(true));

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe"),
      );
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == "VIDEO" && deferredElement.getAttribute("autoplay")) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute("style");
      deferredElement.setAttribute("style", "display: block;");
      window.setTimeout(() => {
        deferredElement.setAttribute("style", formerStyle);
      }, 0);
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0,
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link"),
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this)),
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener("click", this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add("slider-counter__link--active");
    this.sliderControlButtons[this.currentPage - 1].setAttribute("aria-current", true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow,
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow,
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) || (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", swatchValue);
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", "unset");
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset",
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll("select option[selected], fieldset input:checked")).map(
      ({ dataset }) => dataset.optionValueId,
    );
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" },
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this),
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace("[min]", event.target.dataset.min),
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace("[max]", event.target.max),
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace("[step]", event.target.step),
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      event.target.setAttribute("value", inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance";

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }
}

class SiteHeader extends HTMLElement {
  constructor() {
    super();

    this.section_wrapper = this.closest("header");
    this.outer_wrapper = this.closest(".site-header__outer");
    this.header_items = Array.from(this.children).filter((child) => {
      return !child.classList.contains("drawer");
    });

    this.events = [
      {
        el: document,
        type: "cart:changed",
        callback: this.updateCartCount.bind(this),
      },
      {
        el: window,
        type: "scroll",
        callback: this.stickyNavigation.bind(this),
      },
      {
        el: window,
        type: "resize",
        callback: this.collapseNavigation.bind(this),
      },
      {
        el: document,
        type: "DOMContentLoaded",
        callback: this.collapseNavigation.bind(this),
      },
      {
        el: document.fonts,
        type: "loadingdone",
        callback: this.collapseNavigation.bind(this),
      },
    ];
  }

  connectedCallback() {
    this.handleEventListeners("add");
    this.collapseNavigation();
    document.fonts.ready.then(() => {
      if (this.isConnected) this.collapseNavigation();
    });
  }

  disconnectedCallback() {
    this.handleEventListeners("remove");
  }

  handleEventListeners(type = "add") {
    for (const event of this.events) {
      if (type === "add") {
        event.el.addEventListener(event.type, event.callback);
      } else {
        event.el.removeEventListener(event.type, event.callback);
      }
    }
  }

  updateCartCount(e) {
    const cartCountElem = this.querySelector(".site-header__icon.icon--cart");
    const header = e.detail?.sections?.header;
    if (!header) return;

    const html = new DOMParser()
      .parseFromString(header, "text/html")
      .querySelector(".site-header__icon.icon--cart");

    if (html) cartCountElem.innerHTML = html.innerHTML;
  }

  stickyNavigation() {
    const wrapper = this.section_wrapper.getBoundingClientRect();
    if (window.scrollY > wrapper.top) {
      this.outer_wrapper.classList.add("site-header__sticky");
    } else {
      this.outer_wrapper.classList.remove("site-header__sticky");
    }
  }

  collapseNavigation() {
    this.outer_wrapper.classList.remove("site-header__collapse");

    if (window.matchMedia("(max-width: 1024px)").matches) {
      this.outer_wrapper.classList.add("site-header__collapse");
    } else {
      const styles = window.getComputedStyle(this);
      const padding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      const requiredWidth = this.header_items.reduce((width, item) => {
        if (item.matches("main-navigation")) {
          const links = item.querySelector(".main-navigation__inner").children;
          return (
            width +
            Array.from(links).reduce((linkWidth, link) => {
              const linkStyles = window.getComputedStyle(link);
              return (
                linkWidth +
                link.getBoundingClientRect().width +
                parseFloat(linkStyles.marginLeft) +
                parseFloat(linkStyles.marginRight)
              );
            }, 0)
          );
        }
        return width + item.scrollWidth;
      }, 0);

      this.outer_wrapper.classList.toggle(
        "site-header__collapse",
        this.clientWidth - padding < requiredWidth,
      );
    }
    this.handleStickyProduct();
  }
  handleStickyProduct() {
    const product_meta = document.querySelector(".main-product__meta");
    const product_gallery = document.querySelector("media-gallery");
    if (!product_meta && !product_gallery) return;
    product_meta ? (product_meta.style.top = this.offsetHeight + 20 + "px") : null;
    product_gallery ? (product_gallery.style.top = this.offsetHeight + 20 + "px") : null;
  }
}

customElements.define("site-header", SiteHeader);

class DrawerElement extends HTMLElement {
  constructor() {
    super();
    this.is_open = false;
    this.drawer = this.querySelector("details");
    this.drawer_body = this.querySelector("[target-drawer-content]");
    this.main_button = this.querySelector("[action-open-drawer]");
    this.events = [
      {
        el: this,
        evt: "click",
        callback: this.clickHandler.bind(this),
      },
      {
        el: document,
        evt: "click",
        callback: this.outsideClickHandler.bind(this),
      },
      {
        el: this,
        evt: "keydown",
        callback: this.keyboardHandler.bind(this),
      },
    ];
  }

  connectedCallback() {
    this.handleEventListeners();
  }
  disconnectedCallback() {
    this.handleEventListeners("remove");
  }

  handleEventListeners(type = "add") {
    for (const event of this.events) {
      if (type === "add") {
        event.el.addEventListener(event.evt, event.callback);
      } else {
        event.el.removeEventListener(event.evt, event.callback);
      }
    }
  }

  clickHandler(e) {
    if (e.target.closest("[action-open-drawer]")) {
      e.preventDefault();
      this.toggleMenu();
    }
    if (e.target.closest("[action-close-drawer]")) {
      this.setHidden();
    }
  }

  outsideClickHandler(e) {
    if (e.target.closest(".drawer") !== this) {
      if (this.is_open) {
        e.preventDefault();
        this.setHidden();
      }
    }
  }

  keyboardHandler(e) {
    if (e.key === "Escape") {
      this.setHidden();
    }
  }

  toggleMenu() {
    if (!this.is_open) {
      this.setVisible();
    } else {
      this.setHidden();
    }
  }

  setHidden() {
    this.is_open = false;
    this.main_button.setAttribute("aria-expanded", "false");
    this.classList.remove("drawer-open");
    onAnimationEnd(this.drawer_body, () => {
      this.drawer.removeAttribute("open");
    });
  }

  setVisible() {
    this.drawer.setAttribute("open", "");
    this.is_open = true;
    this.main_button.setAttribute("aria-expanded", "true");
    this.classList.add("drawer-open");
  }
}

customElements.define("drawer-element", DrawerElement);

class NavigationMenuDrawer extends DrawerElement {
  constructor() {
    super();
  }

  clickHandler(e) {
    super.clickHandler(e);
    if (e.target.closest(".menu-drawer__button[aria-expanded]")) {
      this.toggleSubMenu(e);
    }
  }

  keyboardHandler(e) {
    super.keyboardHandler(e);
    if (e.code === "Space") {
      if (e.target.classList.contains("menu-drawer__button")) {
        this.toggleSubMenu(e.target);
      }
    }
  }

  checkFocus() {
    const activeLink = document.activeElement;
    const parent = activeLink.closest(".menu-drawer__parent-item");
    if (!parent || parent !== this.activeParent) {
      this.closeAllMenus();
    }
    if (parent && parent.querySelector('[aria-expanded="false"]')) {
      this.openSubMenu(parent.querySelector('[aria-expanded="false"]'));
      this.activeParent = parent;
    }
  }

  toggleSubMenu(e) {
    const target = e.target.closest(".menu-drawer__button[aria-expanded]");
    if (target.getAttribute("aria-expanded") === "false") {
      this.openSubMenu(target);
    } else {
      this.closeSubMenu(target);
    }
  }

  openSubMenu(target) {
    target.setAttribute("aria-expanded", true);
    const menu = target.nextElementSibling;
    menu.style.height = menu.scrollHeight + "px";
  }

  closeSubMenu(target) {
    target.setAttribute("aria-expanded", false);
    const menu = target.nextElementSibling;
    menu.style.height = "0px";
  }

  closeAllMenus() {
    this.querySelectorAll(".menu-drawer__button[aria-expanded]").forEach((menu) => {
      this.closeSubMenu(menu);
    });
  }

  setHidden() {
    super.setHidden();
    this.closeAllMenus();
  }
}
customElements.define("navigation-menu-drawer", NavigationMenuDrawer);

class MainNavigation extends HTMLElement {
  constructor() {
    super();

    this.parents = this.querySelectorAll(".main-navigation__parent-item");
    this.is_touch = false;
    if (window.matchMedia("(pointer: coarse)").matches) {
      this.is_touch = true;
    }
    this.events = [
      {
        el: this,
        evt: "keydown",
        callback: this.keyboardHandler.bind(this),
      },
    ];

    for (const parent of this.parents) {
      if (this.is_touch) {
        this.events.push({
          el: parent,
          evt: "click",
          callback: this.clickHandler.bind(this),
        });
      } else {
        this.events.push(
          {
            el: parent,
            evt: "mouseenter",
            callback: this.mouseHandler.bind(this),
          },
          {
            el: parent,
            evt: "mouseleave",
            callback: this.mouseHandler.bind(this),
          },
        );
      }
    }

    if (this.is_touch) {
      this.events.push({
        el: document,
        evt: "click",
        callback: this.outsideClickHandler.bind(this),
      });
    }
  }

  connectedCallback() {
    this.handleEventListeners();
  }
  disconnectedCallback() {
    this.handleEventListeners("remove");
  }

  handleEventListeners(type = "add") {
    for (const event of this.events) {
      if (type === "add") {
        event.el.addEventListener(event.evt, event.callback);
      } else {
        event.el.removeEventListener(event.evt, event.callback);
      }
    }
  }

  clickHandler(e) {
    if (
      e.target.classList.contains("main-navigation__parent-button") &&
      e.target.getAttribute("aria-expanded") === "false"
    ) {
      e.preventDefault();
      this.closeAllMenus();
      e.target.setAttribute("aria-expanded", "true");
    }
  }

  outsideClickHandler(e) {
    if (e.target.closest("main-navigation") !== this) {
      this.closeAllMenus();
    }
  }

  mouseHandler(e) {
    if (e.type === "mouseleave") {
      e.target.querySelector("[aria-expanded]")?.setAttribute("aria-expanded", "false");
    } else if (e.type === "mouseenter") {
      e.target.querySelector("[aria-expanded]")?.setAttribute("aria-expanded", "true");
    }
  }

  openMenu(target) {
    if (this.activeParent !== target) {
      // Close the active menu before opening the next
      this.activeParent?.setAttribute("aria-expanded", false);

      target.setAttribute("aria-expanded", true);
      this.activeParent = target;
    }
  }

  keyboardHandler(e) {
    if (e.key === "Enter" || e.key === "Return") {
      if (e.target.getAttribute("aria-expanded") === "false") {
        e.preventDefault();
        this.closeAllMenus();
        e.target.setAttribute("aria-expanded", "true");
      } else {
        e.target.setAttribute("aria-expanded", "false");
      }
    }
    if (e.key === "Escape") {
      this.parents.forEach((parent) => {
        parent.querySelector("[aria-expanded]")?.setAttribute("aria-expanded", "false");
      });
    }
  }

  closeAllMenus() {
    this.parents.forEach((parent) => {
      parent.querySelector("[aria-expanded]")?.setAttribute("aria-expanded", "false");
    });
  }
}
customElements.define("main-navigation", MainNavigation);

class DialogModal extends HTMLElement {
  constructor() {
    super();
    this.is_open = false;
    this.dialog = this.querySelector("details");
    this.dialog_body = this.querySelector("[target-dialog-content]");
    this.main_button = this.querySelector("[action-open-dialog]");
    this.events = [
      {
        el: this,
        evt: "click",
        callback: this.clickHandler.bind(this),
      },
      {
        el: document,
        evt: "click",
        callback: this.outsideClickHandler.bind(this),
      },
      {
        el: this,
        evt: "keydown",
        callback: this.keyboardHandler.bind(this),
      },
    ];
  }

  connectedCallback() {
    this.handleEventListeners();
  }
  disconnectedCallback() {
    this.handleEventListeners("remove");
  }

  handleEventListeners(type = "add") {
    for (const event of this.events) {
      if (type === "add") {
        event.el.addEventListener(event.evt, event.callback);
      } else {
        event.el.removeEventListener(event.evt, event.callback);
      }
    }
  }

  clickHandler(e) {
    if (e.target.closest("[action-open-dialog]")) {
      e.preventDefault();
      this.toggleDialog();
    }
    if (e.target.closest("[action-close-dialog]")) {
      this.setHidden();
    }
  }

  outsideClickHandler(e) {
    if (e.target.closest(".dialog-modal") !== this) {
      if (this.is_open) {
        e.preventDefault();
        this.setHidden();
      }
    }
  }

  keyboardHandler(e) {
    if (e.key === "Escape") {
      this.setHidden();
    }
  }

  toggleDialog() {
    if (!this.is_open) {
      this.setVisible();
    } else {
      this.setHidden();
    }
  }

  setHidden() {
    this.is_open = false;
    this.main_button.setAttribute("aria-expanded", "false");
    this.classList.remove("dialog-open");
    onAnimationEnd(this.dialog_body, () => {
      this.dialog.removeAttribute("open");
    });
  }

  setVisible() {
    this.dialog.setAttribute("open", "");
    this.is_open = true;
    this.main_button.setAttribute("aria-expanded", "true");
    this.classList.add("dialog-open");
    if (this.querySelector("predictive-search")) {
      setTimeout(() => {
        const search_input = this.querySelector("predictive-search input");

        search_input.focus();
        search_input.dispatchEvent(new Event("focus"));
      }, 300);
    }
  }
}

customElements.define("dialog-modal", DialogModal);

class MainPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.is_active = false;
    this.search = {
      query: this.getAttribute("search-query"),
      type: this.getAttribute("search-type") || "product,query,collection",
      limit: this.getAttribute("search-scope") || "10",
      scope: this.getAttribute("search-scope") || "each",
      fields:
        this.getAttribute("search-fields") ||
        "title,product_type,variants.title,variants.sku,variants.barcode,tag",
      unavailable: this.getAttribute("search-unavailable") || "last",
    };
    this.results = this.querySelector("#main-predictive-search-results");
    this.input_wrapper = this.querySelector(".input-wrapper");
    this.input = this.querySelector('input[type="search"]');

    this.events = [
      {
        evt: "click",
        el: this,
        callback: this.clickHandler.bind(this),
      },
      {
        evt: "input",
        el: this.input,
        callback: this.debounce((event) => {
          this.inputHandler(event);
        }, 300).bind(this),
      },
    ];
  }

  connectedCallback() {
    this.handleEventListeners();
    this.setResultsMaxHeight();
  }

  handleEventListeners(type = "add") {
    for (const event of this.events) {
      if (type === "add") {
        event.el.addEventListener(event.evt, event.callback);
      } else {
        event.el.removeEventListener(event.evt, event.callback);
      }
    }
  }

  clickHandler(e) {
    if (e.target.classList.contains("input-clear")) {
      this.input.value = "";
      this.input.dispatchEvent(new Event("input"));
      this.input.focus();
    }
  }

  attributeChangedCallback(name, prev, next) {
    if (prev === next) return;
    if (next === "") {
      this.closeResultsPane();
      this.results.innerHTML = "";
      return;
    }

    this.getNewSearch();
  }

  static get observedAttributes() {
    return ["search-query"];
  }

  inputHandler() {
    const query = this.input.value.trim();
    this.search.query = query;
    this.setAttribute("search-query", query);
  }

  async getNewSearch() {
    this.loading_el = this.querySelector(".main-predictive-search__loading");
    this.loading_el ? this.loading_el.setAttribute("aria-hidden", "false") : null;
    const string = `/search/suggest?q=${this.search.query}&resources[type]=${this.search.type}&resources[limit]=${this.search.limit}&resources[limit_scope]=${this.search.scope}&resources[options][fields]=${this.search.fields}&resources[options][unavailable_products]=${this.search.unavailable}&section_id=predictive-search`;
    try {
      const response = await fetch(string);
      if (!response.ok) {
        throw new Error(response.status);
      }
      const data = await response.text();
      const markup = new DOMParser()
        .parseFromString(data, "text/html")
        .querySelector("#shopify-section-predictive-search").innerHTML;
      this.results.innerHTML = markup;
      this.openResultsPane();
    } catch (error) {
      this.closeResultsPane();
    }
    this.loading_el ? this.loading_el.setAttribute("aria-hidden", "false") : null;
  }

  setResultsMaxHeight() {
    this.results.removeAttribute("style");
    const results_rect = this.results.getBoundingClientRect();
    if (this.results.scrollHeight + results_rect.top + 20 > window.innerHeight) {
      this.results.style.height = window.innerHeight - 20 - results_rect.top + "px"; // remove an extra 20px from height to space from bottom of browser window
    }
  }

  openResultsPane() {
    this.setResultsMaxHeight();
    if (this.results.innerHTML !== "") {
      this.results.classList.add("main-predictive-search-results--visible");
    }
  }

  closeResultsPane() {
    this.results.classList.remove("main-predictive-search-results--visible");
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define("main-predictive-search", MainPredictiveSearch);

class QuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.timer = null;
    this.key = this.getAttribute("data-key") || null;
    this.input = this.querySelector(".quantity-selector__input");
    this.quantity = parseInt(this.input.value) || 1;

    // Events
    this.events = [
      {
        el: this,
        type: "click",
        callback: this.eventHandler.bind(this),
      },
    ];
    if (this.input.getAttribute("readonly")) return;
    this.events.push({
      el: this,
      type: "change",
      callback: this.eventHandler.bind(this),
    });
  }

  connectedCallback() {
    this.handleEventListeners("add");
  }
  disconnectedCallback() {
    this.handleEventListeners("remove");
  }

  handleEventListeners(type = "add") {
    for (const evt of this.events) {
      if (type === "add") {
        evt.el.addEventListener(evt.type, evt.callback);
      } else {
        evt.el.removeEventListener(evt.type, evt.callback);
      }
    }
  }

  eventHandler(e) {
    e.preventDefault();
    this.updateQuantity(e);
  }

  updateQuantity(e) {
    if (e.type === "click") {
      if (e.target.classList.contains("quantity-selector__decrease")) {
        this.quantity = Math.max(1, parseInt(this.quantity) - 1);
      } else if (e.target.classList.contains("quantity-selector__increase")) {
        this.quantity = parseInt(this.quantity) + 1;
      }
      if (parseInt(this.input.value) === this.quantity) return;
    } else if (e.type === "change") {
      this.quantity = this.input.value.replace(/\D/g, "");
      if (this.quantity === "") {
        this.quantity = 1;
      } else {
        this.quantity = Math.max(1, parseInt(this.quantity));
      }
    }

    this.input.value = this.quantity;

    if (!this.key) return;
    this.debounce(() => {
      const event = new CustomEvent("quantity:changed", {
        bubbles: true,
        detail: {
          key: this.key,
          quantity: this.quantity,
        },
      });
      this.dispatchEvent(event);
    });
  }

  debounce(fn, wait = 500) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      fn.apply(this);
    }, wait);
  }
}
customElements.define("quantity-selector", QuantitySelector);

// Horizon function
function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const animations = Array.isArray(elements)
    ? elements.flatMap((element) => element.getAnimations(options))
    : elements.getAnimations(options);
  const animationPromises = animations.reduce((acc, animation) => {
    if (animation.timeline instanceof DocumentTimeline) {
      acc.push(animation.finished);
    }

    return acc;
  }, []);

  return Promise.allSettled(animationPromises).then(callback);
}

class RecommendationsCarousel extends HTMLElement {
  constructor() {
    super();

    this.defaultOptions = {
      loop: true,
    };

    this.elements = {
      swiper: this.querySelector(".main-carousel"),
      pagination: this.querySelector(".main-carousel__pagination"),
      prevButton: this.querySelector(".main-carousel__button-prev"),
      nextButton: this.querySelector(".main-carousel__button-next"),
    };

    this.swiperOptions = {
      ...this.defaultOptions,
    };
    if (this.elements.swiper.dataset.carouselOptions) {
      this.swiperOptions = {
        ...this.defaultOptions,
        ...JSON.parse(this.elements.swiper.dataset.carouselOptions),
      };
    }

    if (this.elements.prevButton && this.elements.nextButton) {
      this.swiperOptions.navigation = {
        nextEl: this.elements.nextButton,
        prevEl: this.elements.prevButton,
      };
    }

    if (this.elements.pagination) {
      this.swiperOptions.pagination = {
        el: this.elements.pagination,
      };
    }

    this.limit = this.getAttribute("limit") || "8";
    this.intent = this.getAttribute("intent") || "related";
    this.product_id = this.getAttribute("product_id");
    this.section_id = this.getAttribute("section_id");
  }

  connectedCallback() {
    this.addEventListeners();
    this.init();
  }

  addEventListeners() {}

  async init() {
    if (!this.elements.swiper) return;
    const base_url = `${window.Shopify.routes.root}recommendations/products?section_id=${this.section_id}&product_id=${this.product_id}&limit=${this.limit}`;
    if (this.intent === "both") {
      const complementary = await this.getRecommendations(`${base_url}&intent=complementary`);
      if (complementary) {
        this.renderRecommendations(complementary);
      } else {
        const related = await this.getRecommendations(`${base_url}&intent=related`);
        if (related) {
          this.renderRecommendations(related);
        } else {
          this.style.display = "none";
        }
      }
    } else {
      const recommendations = await this.getRecommendations(`${base_url}&intent=${this.intent}`);
      if (recommendations) {
        this.renderRecommendations(recommendations);
      }
    }
  }

  async getRecommendations(url) {
    try {
      const response = await fetch(url);
      const data = await response.text();
      const html = new DOMParser()
        .parseFromString(data, "text/html")
        .querySelector("recommendations-carousel .main-carousel__wrapper");
      if (html.children.length > 0) {
        return html.innerHTML;
      } else {
        return null;
      }
    } catch (error) {
      console.log(error);
    }
  }

  renderRecommendations(data) {
    this.elements.swiper.querySelector(".main-carousel__wrapper").innerHTML = data;
    this.parentElement.style.display = "block";
    this.swiper = new Swiper(this.elements.swiper, this.swiperOptions);
  }
}
customElements.define("recommendations-carousel", RecommendationsCarousel);

class MainCarousel extends HTMLElement {
  constructor() {
    super();

    this.delay_load =
      this.getAttribute("data-load-delay") && this.getAttribute("data-load-delay") === "true"
        ? true
        : false;

    this.defaultOptions = {
      loop: true,
    };

    this.elements = {
      swiper: this.querySelector(".main-carousel"),
      pagination: this.querySelector(".main-carousel__pagination"),
      prevButton: this.querySelector(".main-carousel__button-prev"),
      nextButton: this.querySelector(".main-carousel__button-next"),
    };

    this.swiperOptions = {
      ...this.defaultOptions,
    };
    if (this.elements.swiper.dataset.carouselOptions) {
      this.swiperOptions = {
        ...this.defaultOptions,
        ...JSON.parse(this.elements.swiper.dataset.carouselOptions),
      };
    }

    if (this.elements.prevButton && this.elements.nextButton) {
      this.swiperOptions.navigation = {
        nextEl: this.elements.nextButton,
        prevEl: this.elements.prevButton,
      };
    }

    if (this.elements.pagination) {
      this.swiperOptions.pagination = {
        el: this.elements.pagination,
      };
    }
  }

  connectedCallback() {
    this.addEventListeners();
  }

  addEventListeners() {
    if (this.delay_load) {
      this.init();
    } else {
      document.addEventListener("DOMContentLoaded", this.init.bind(this));
    }
  }

  init() {
    if (!this.elements.swiper) return;
    this.swiper = new Swiper(this.elements.swiper, this.swiperOptions);
  }
}
customElements.define("main-carousel", MainCarousel);

class RecentlyViewedCarousel extends MainCarousel {
  constructor() {
    super();
    this.recentlyViewedHandles = JSON.parse(localStorage.getItem("recentlyViewedItems"));
    this.currentProduct = this.getAttribute("current-product");

    if (this.currentProduct && this.recentlyViewedHandles) {
      this.recentlyViewedHandles = this.recentlyViewedHandles.filter((handle) => {
        return handle != this.currentProduct;
      });
    }
  }

  connectedCallback() {
    this.addEventListeners();
  }

  addEventListeners() {
    document.addEventListener("DOMContentLoaded", this.fetchRecentlyViewed.bind(this));
  }

  async fetchRecentlyViewed() {
    if (!this.recentlyViewedHandles) return;

    try {
      const res = await fetch(
        `/search?view=recently-viewed-items&q=${this.recentlyViewedHandles.join(",")}`,
      );
      const data = await res.text();
      if (!data) return;
      this.renderRecentlyViewed(data);
    } catch (error) {
      console.log(error);
    }
  }

  renderRecentlyViewed(data) {
    const items = new DOMParser()
      .parseFromString(data, "text/html")
      .querySelector(".main-carousel__wrapper").innerHTML;
    const item_wrapper = this.querySelector(".main-carousel__wrapper");
    item_wrapper.innerHTML = items;
    this.init();
    this.parentElement.style.display = "block";
  }
}
customElements.define("recently-viewed-carousel", RecentlyViewedCarousel);

async function addUpsellToCart(e) {
  if (!e || !e.target || !e.target.dataset) return;

  const productId = e.target.dataset.productId;
  const parentKey = e.target.dataset.parentKey;
  const parentProperties = e.target.closest(".crt-Products_Item").querySelector("[data-properties]")
    ? JSON.parse(
        e.target.closest(".crt-Products_Item").querySelector("[data-properties]").dataset
          .properties,
      )
    : null;
  const timeStampKey = Date.now();

  if (!productId || !parentKey || !parentProperties) return;

  e.target.closest(".crt-upsells").classList.add("adding-upsell");
  parentProperties._time_stamp_key = timeStampKey;

  try {
    const mutate = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id: parentKey,
        properties: parentProperties,
      }),
    });

    if (!mutate.ok) {
      const errorData = await mutate.json();
      throw new Error(errorData.description || "Failed to update item property");
    }

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id: productId,
        quantity: 1,
        properties: {
          _parent_time_stamp_key: timeStampKey,
          _is_upsell: true,
        },
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || "Failed to add item to cart");
    }

    const data = await response.json();
    console.log("Item added to cart:", data);
    // hide upsells if successfully added to cart
    e.target.closest(".crt-upsells").classList.add("upsell-in-cart");
    location.reload();
    return data;
  } catch (error) {
    console.error("Error adding item to cart:", error.message);
    e.target.closest(".crt-upsells").classList.remove("adding-upsell");
    return null;
  }
}

async function removeUpsellFromCart(e) {
  if (!e || !e.target) return;

  const productId = e.target.closest(".crt-Products_Remove").dataset.productId;

  if (!productId) return;

  try {
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id: productId,
        quantity: 0,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || "Failed to remove item from cart");
    }

    const data = await response.json();
    console.log("Item removed from cart:", data);
    location.reload();
    return data;
  } catch (error) {
    console.error("Error removing item from cart:", error.message);
    return null;
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const upsellATCButtons = document.querySelectorAll(".crt-upsells__product-ATC");
  upsellATCButtons.forEach((btn) => {
    btn.addEventListener("click", addUpsellToCart);
  });
});

document.addEventListener("DOMContentLoaded", () => {
  const upsellRemoveButtons = document.querySelectorAll(".crt-Products_Remove");
  upsellRemoveButtons.forEach((btn) => {
    btn.addEventListener("click", removeUpsellFromCart);
  });
});

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.events = [
      {
        el: this,
        type: "submit",
        callback: this.submitHandler.bind(this),
      },
    ];
  }

  connectedCallback() {
    this.handleEventListeners("add");
  }
  disconnectedCallback() {
    this.handleEventListeners("remove");
  }
  handleEventListeners(type) {
    for (const evt of this.events) {
      if (type === "add") {
        evt.el.addEventListener(evt.type, evt.callback);
      } else {
        evt.el.removeEventListener(evt.type, evt.callback);
      }
    }
  }
  async submitHandler(e) {
    e.preventDefault();
    if (e.submitter) {
      e.submitter.classList.add("btn--loading");
    }
    const products = [];
    const formData = new FormData(this.querySelector("form"));

    const item = {};
    for (const [key, val] of formData.entries()) {
      if (key === "id") {
        item.id = +val;
      }
      if (key === "quantity") {
        item.quantity = +val;
      }
    }
    item.quantity = item.quantity !== 0 ? item.quantity : 1;
    products.push(item);
    const body = { items: products, sections: "header" };
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body), // expects {items: [{...}]}
      });
      if (!res.ok) {
        throw new Error({ message: "Something went wrong" });
      }
      if (e.submitter) {
        e.submitter.classList.add("btn--transition");
        e.submitter.innerHTML = theme.strings.addedToCart;
      }
      const data = await res.json();
      document.dispatchEvent(new CustomEvent("cart:changed", { detail: data }));
      if (
        theme.showUpsellsPage &&
        window.location.pathname === "/collections/our-favourite-and-classic-cocktails"
      ) {
        const giftParent = data.items?.[0]?.key;
        const finishingTouchesUrl = giftParent
          ? `/pages/finishing-touches?gift_parent=${encodeURIComponent(giftParent)}`
          : "/pages/finishing-touches";
        setTimeout(() => {
          window.location.href = finishingTouchesUrl;
        }, 100);
      }
    } catch (err) {
      console.log(err);
    } finally {
      if (e.submitter) {
        e.submitter.classList.remove("btn--loading");
        setTimeout(() => {
          e.submitter.innerHTML = theme.strings.addToCart;
          e.submitter.classList.remove("btn--transition");
        }, 1000);
      }
    }
  }
}
customElements.define("product-card", ProductCard);

class FinishingGiftWrap extends HTMLElement {
  constructor() {
    super();
    this.events = [
      {
        el: this,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
    ];
  }

  connectedCallback() {
    this.section = this.closest(".shopify-section");
    this.parentKey = new URLSearchParams(window.location.search).get("gift_parent");
    this.panel = Array.from(this.querySelectorAll("[data-gift-parent-key]")).find(
      (panel) => panel.dataset.giftParentKey === this.parentKey,
    );

    if (!this.panel || this.panel.dataset.hasGiftWrap === "true") return;

    this.panel.hidden = false;
    this.hidden = false;
    this.section?.classList.add("finishing-gift-wrap--visible");
    this.handleEventListeners("add");
  }

  disconnectedCallback() {
    this.handleEventListeners("remove");
  }

  handleEventListeners(type) {
    for (const event of this.events) {
      if (type === "add") {
        event.el.addEventListener(event.type, event.callback);
      } else {
        event.el.removeEventListener(event.type, event.callback);
      }
    }
  }

  clickHandler(e) {
    const button = e.target.closest("[data-gift-wrap-add]");
    if (!button || !this.contains(button)) return;
    this.addGiftWrap(button);
  }

  generateGiftRef() {
    return `gw-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  }

  setLoading(isLoading, button) {
    if (!button) return;
    button.disabled = isLoading;
    button.classList.toggle("btn--loading", isLoading);
  }

  updateHeader(sectionHtml) {
    if (!sectionHtml) return;

    const nextIcon = new DOMParser()
      .parseFromString(sectionHtml, "text/html")
      .querySelector(".site-header__icon.icon--cart");
    const currentIcon = document.querySelector(".site-header__icon.icon--cart");
    if (nextIcon && currentIcon) currentIcon.innerHTML = nextIcon.innerHTML;
  }

  setAddedState(button) {
    this.setLoading(false, button);
    this.querySelectorAll("[data-gift-wrap-add]").forEach((wrapButton) => {
      wrapButton.disabled = true;
      wrapButton.classList.add("btn-style__nostock");
      wrapButton.textContent = this.dataset.successMessage;
    });
    this.classList.add("finishing-gift-wrap--added");
  }

  async addGiftWrap(button) {
    const wrapId = button.dataset.variantId;
    if (!this.parentKey || !wrapId) return;

    this.setLoading(true, button);

    let parentUpdated = false;
    let updatedParentKey = this.parentKey;
    let parentQuantity = 1;
    let rollbackProperties = { _gift_wrap_removed: "true" };

    try {
      const cartResponse = await fetch("/cart.js");
      if (!cartResponse.ok) throw new Error("Could not load the cart");

      const cart = await cartResponse.json();
      const parent = cart.items.find((item) => item.key === this.parentKey);
      if (!parent) throw new Error("Could not find the parent product");

      const existingWrap = cart.items.find(
        (item) =>
          parent.properties?._gift_ref &&
          item.properties?._gift_ref === parent.properties._gift_ref &&
          item.properties?.["Gift wrap for"],
      );
      if (existingWrap) {
        this.setAddedState(button);
        return;
      }

      parentQuantity = parent.quantity;
      const originalProperties = { ...(parent.properties || {}) };
      delete originalProperties._gift_wrap_removed;
      if (Object.keys(originalProperties).length > 0) {
        rollbackProperties = originalProperties;
      }

      const ref = originalProperties._gift_ref || this.generateGiftRef();
      const parentProperties = {
        ...originalProperties,
        "Gift Wrap": "Yes",
        _gift_ref: ref,
        _gift_wrap_style: button.dataset.wrapName || "Gift wrap",
      };

      const parentResponse = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          id: this.parentKey,
          quantity: parent.quantity,
          properties: parentProperties,
        }),
      });
      if (!parentResponse.ok) throw new Error("Could not link the gift wrap");

      const parentCart = await parentResponse.json();
      const updatedParent = parentCart.items.find(
        (item) => item.properties?._gift_ref === ref && item.variant_id === parent.variant_id,
      );
      if (!updatedParent) throw new Error("Could not find the updated gift-wrap product");

      updatedParentKey = updatedParent.key;
      parentUpdated = true;

      const wrapResponse = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: wrapId,
              quantity: parent.quantity,
              properties: {
                "Gift wrap for": this.panel.dataset.parentTitle || parent.product_title,
                _gift_ref: ref,
              },
            },
          ],
          sections: "header",
        }),
      });
      if (!wrapResponse.ok) throw new Error("Could not add the gift wrap");

      const result = await wrapResponse.json();
      const url = new URL(window.location.href);
      url.searchParams.set("gift_parent", updatedParentKey);
      window.history.replaceState({}, "", url);
      this.parentKey = updatedParentKey;
      this.updateHeader(result.sections?.header);
      this.setAddedState(button);
    } catch (error) {
      if (parentUpdated) {
        try {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({
              id: updatedParentKey,
              quantity: parentQuantity,
              properties: rollbackProperties,
            }),
          });
        } catch (rollbackError) {
          console.log("Could not roll back gift-wrap properties", rollbackError);
        }
      }
      this.setLoading(false, button);
      console.log("Something went wrong", error);
    }
  }
}

customElements.define("finishing-gift-wrap", FinishingGiftWrap);

class QuickView extends HTMLElement {
  constructor() {
    super();

    this.details_container = this.querySelector(".quick-view__details");
    this.events = [
      {
        el: this,
        event: "click",
        callback: this.clickHandler.bind(this),
      },
    ];
  }

  connectedCallback() {
    this.handleEventListeners("add");
  }

  disconnectedCallback() {
    this.handleEventListeners("remove");
  }

  handleEventListeners(type = "add") {
    for (const evt of this.events) {
      if (type === "add") {
        evt.el.addEventListener(evt.event, evt.callback);
      } else {
        evt.el.removeEventListener(evt.event, evt.callback);
      }
    }
  }

  clickHandler(e) {
    if (e.target === this.querySelector("summary")) {
      this.fetchProductDetails();
    }
    if (e.target.dataset.action === "close" || e.target.classList.contains("quick-view-backdrop")) {
      this.querySelector("details.quick-view[open]")?.removeAttribute("open");
    }
  }

  async fetchProductDetails() {
    if (this.cached_details || !this.details_container) return;

    this.details_container.classList.add("details-loading");
    try {
      const res = await fetch(
        `/products/${this.dataset.productHandle}?section_id=quick-view-details`,
      );
      if (!res.ok) {
        throw new Error("Something went wrong");
      }
      const data = await res.text();
      const html = new DOMParser()
        .parseFromString(data, "text/html")
        .querySelector(".quick-view__details").innerHTML;

      this.details_container.innerHTML = html;
      if (this.closest("build-a-box")) {
        const current_box = this.closest("build-a-box");
        const button = this.querySelector("button[data-cocktail]");
        if (current_box.cocktails[button.dataset.cocktail]) {
          button.innerHTML = theme.strings.addAnother;
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      this.details_container.classList.remove("details-loading");
      this.cached_details = true;
    }
  }
}
customElements.define("quick-view", QuickView);

class QuickViewMedia extends HTMLElement {
  constructor() {
    super();

    this.container = this.querySelector(".quick-view__media-container");
    this.images = this.querySelectorAll(".quick-view__image");
    this.events = [
      {
        el: this.container,
        evt: "scrollend",
        callback: this.detectActiveMedia.bind(this),
      },
    ];
  }
  connectedCallback() {
    if (!this.querySelector(".quick-view__media-pagination")) return;
    this.handleEventListeners();
  }
  disconnectedCallback() {
    if (!this.querySelector(".quick-view__media-pagination")) return;
    this.handleEventListeners("remove");
  }
  handleEventListeners(type = "add") {
    for (const event of this.events) {
      if (type === "add") {
        event.el.addEventListener(event.evt, event.callback);
      } else {
        event.el.removeEventListener(event.evt, event.callback);
      }
    }
  }
  detectActiveMedia() {
    // Remove active dot class
    this.querySelector(".dot--active")?.classList.remove("dot--active");

    let closestIndex = null;
    let closestDistance = Infinity;

    for (const [index, image] of Object.entries(this.images)) {
      const distance = Math.abs(
        image.getBoundingClientRect().left - this.container.getBoundingClientRect().left,
      );
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    }

    if (closestIndex !== null) {
      const activeDot = this.querySelector(`.quick-view__dot[data-index="${closestIndex}"]`);
      activeDot?.classList.add("dot--active");
    }
  }
}
customElements.define("quick-view-media", QuickViewMedia);

class AgeVerification extends HTMLElement {
  constructor() {
    super();

    this.events = [
      {
        el: this.querySelector("button"),
        evt: "click",
        callback: this.clickHandler.bind(this),
      },
    ];
  }

  connectedCallback() {
    const verified = this.userVerification;
    if (!verified.value) {
      this.setAttribute("aria-hidden", false);
      this.setAttribute("tabindex", "-1");
      this.focus();
      trapFocus(this);
      this.handleEventListeners();
    }
  }

  handleEventListeners(type = "add") {
    for (const event of this.events) {
      if (type === "add") {
        event.el.addEventListener(event.evt, event.callback);
      } else {
        event.el.removeEventListener(event.evt, event.callback);
      }
    }
  }

  clickHandler(e) {
    this.closeDialog();
    const verified = this.userVerification;
    this.storeUserVerification(verified);
  }

  storeUserVerification(age_verification) {
    age_verification.value = 1;

    if (!Shopify.designMode) {
      localStorage.setItem("age_verified", JSON.stringify(age_verification));
    }
  }

  get userVerification() {
    const now = Date.now();
    const stored_object = localStorage.getItem("age_verified");
    let age_verification = stored_object ? JSON.parse(stored_object) : null;
    if (!age_verification || age_verification.expiry < now) {
      localStorage.removeItem("age_verified");
      age_verification = { value: 0, expiry: now + 2592000000 };
    }
    return age_verification;
  }

  openDialog() {
    setTimeout(() => {
      this.setAttribute("aria-hidden", false);
    }, 300);
  }

  closeDialog() {
    this.setAttribute("aria-hidden", true);
    removeTrapFocus();
  }
}

customElements.define("age-verification", AgeVerification);

// TODO: Remove post live cart checks in 90 days (on 01.09.26)
class MaintenanceDialog extends HTMLElement {
  constructor() {
    super();

    this.events = [
      {
        el: this.querySelector("button"),
        evt: "click",
        callback: this.clickHandler.bind(this),
      },
    ];
  }

  connectedCallback() {
    let trigger = false;
    if (localStorage.getItem("pre_live_cart")) {
      localStorage.removeItem("pre_live_cart");
    }
    if (localStorage.getItem("post_live_cart")) return;

    +this.dataset.cartSize > 0 ? (trigger = true) : localStorage.setItem("post_live_cart", true);

    if (trigger) {
      this.openDialog();
      this.setAttribute("tabindex", "-1");
      this.focus();
      trapFocus(this);
      this.handleEventListeners();
    }
  }

  handleEventListeners(type = "add") {
    for (const event of this.events) {
      if (type === "add") {
        event.el.addEventListener(event.evt, event.callback);
      } else {
        event.el.removeEventListener(event.evt, event.callback);
      }
    }
  }

  clickHandler(e) {
    this.closeDialog();
    this.acknowledgeMaintenance();
  }

  acknowledgeMaintenance() {
    // clear cart
    this.clearCart();
  }

  async clearCart() {
    try {
      const clear = await fetch("/cart/clear.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
      });
      if (!clear.ok) {
        throw new Error("Something went wrong");
      }
      window.location.reload();
    } catch (error) {
      console.log(error);
    }
  }

  openDialog() {
    setTimeout(() => {
      this.setAttribute("aria-hidden", false);
    }, 1000);
  }

  closeDialog() {
    this.setAttribute("aria-hidden", true);
    removeTrapFocus();
  }
}

customElements.define("maintenance-dialog", MaintenanceDialog);

class VideoElement extends HTMLElement {
  constructor() {
    super();
    this.events = [];
  }

  connectedCallback() {
    if (this.hasAttribute("data-responsive-video")) {
      this.initializeResponsiveVideo();
      return;
    }

    this.video = this.querySelector("video");
    this.posterDiv = this.querySelector(".featured-media-slider__video-img");

    if (!this.video || !this.posterDiv) return;

    this.posterDiv.addEventListener("click", () => {
      document.querySelectorAll("video-element").forEach((el) => {
        if (el !== this && el.video && !el.video.paused) {
          el.video.pause();
          el.classList.remove("is-playing");
        }
      });

      this.classList.add("is-playing");
      this.video.play();
    });

    this.video.addEventListener("click", () => {
      if (!this.video.paused) {
        this.video.pause();
        this.classList.remove("is-playing");
      }
    });
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }

  initializeResponsiveVideo() {
    this.mobileMediaQuery = window.matchMedia("(max-width: 767px)");
    this.events = [
      {
        el: this.mobileMediaQuery,
        type: "change",
        callback: this.mediaHandler.bind(this),
      },
    ];

    handleEventListeners(this.events, "add");
    this.loadResponsiveVideo(this.mobileMediaQuery.matches);
  }

  mediaHandler(e) {
    this.loadResponsiveVideo(e.matches);
  }

  loadResponsiveVideo(isMobile) {
    const desktopVideo = this.querySelector("[data-video-desktop]");
    const mobileVideo = this.querySelector("[data-video-mobile]");
    const activeVideo = isMobile && mobileVideo ? mobileVideo : desktopVideo;

    if (!activeVideo || activeVideo === this.video) return;

    this.querySelectorAll("video").forEach((video) => {
      if (video === activeVideo) return;

      video.pause();
      video.querySelectorAll("source").forEach((source) => source.removeAttribute("src"));
      video.removeAttribute("poster");
      video.load();
    });

    activeVideo.querySelectorAll("source[data-src]").forEach((source) => {
      source.src = source.dataset.src;
    });

    if (activeVideo.dataset.poster) {
      activeVideo.poster = activeVideo.dataset.poster;
    }

    this.video = activeVideo;
    activeVideo.load();
    activeVideo.play().catch(() => {});
  }
}

customElements.define("video-element", VideoElement);

class GiftInsertForm extends HTMLElement {
  constructor() {
    super();
    this.events = [];
    this.isLoading = false;
    this.pendingSubmit = false;
  }

  connectedCallback() {
    this.section = this.closest(".gift-insert-form");
    this.visibleForm = this.querySelector("[data-gift-insert-form]");
    this.visibleInput = this.querySelector("[data-gift-insert-email]");
    this.visibleSubmit = this.querySelector(".gift-insert-form__submit");
    this.klaviyoMount = this.querySelector("[data-klaviyo-form]");
    this.codeOutput = this.querySelector("[data-code-template]");

    if (
      !this.section ||
      !this.visibleForm ||
      !this.visibleInput ||
      !this.visibleSubmit ||
      !this.klaviyoMount ||
      !this.codeOutput
    ) {
      return;
    }

    this.events = [
      {
        el: this.visibleInput,
        type: "input",
        callback: this.inputHandler.bind(this),
      },
      {
        el: this.visibleForm,
        type: "submit",
        callback: this.submitHandler.bind(this),
      },
      {
        el: window,
        type: "klaviyoForms",
        callback: this.klaviyoFormsHandler.bind(this),
      },
    ];

    this.klaviyoObserver = new MutationObserver(this.mutationHandler.bind(this));
    this.klaviyoObserver.observe(this.klaviyoMount, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    handleEventListeners(this.events, "add");
    this.mutationHandler();
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
    this.klaviyoObserver?.disconnect();
  }

  inputHandler(e) {
    if (e.target === this.visibleInput) {
      this.syncKlaviyoEmail();
    }
  }

  submitHandler(e) {
    e.preventDefault();

    if (this.isLoading || !this.visibleInput.reportValidity()) return;

    this.pendingSubmit = true;
    this.setLoading(true);
    this.submitKlaviyoForm();
  }

  klaviyoFormsHandler(e) {
    const detail = e.detail || {};

    if (detail.type !== "submit" || detail.formId !== this.dataset.formId) return;

    this.completeSubmission();
  }

  mutationHandler() {
    this.syncKlaviyoEmail();

    if (this.pendingSubmit) {
      this.submitKlaviyoForm();
    }

    this.completeSubmission();
  }

  getKlaviyoEmailInput() {
    return this.klaviyoMount.querySelector(
      'input[type="email"], input[name="email"], input[name="$email"]',
    );
  }

  syncKlaviyoEmail() {
    const klaviyoInput = this.getKlaviyoEmailInput();

    if (!klaviyoInput || klaviyoInput.value === this.visibleInput.value) return;

    const valueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;

    valueSetter?.call(klaviyoInput, this.visibleInput.value);
    klaviyoInput.dispatchEvent(new Event("input", { bubbles: true }));
    klaviyoInput.dispatchEvent(new Event("change", { bubbles: true }));
  }

  submitKlaviyoForm() {
    const klaviyoInput = this.getKlaviyoEmailInput();
    const klaviyoForm = klaviyoInput?.closest("form");

    if (!klaviyoForm) return;

    this.pendingSubmit = false;
    this.syncKlaviyoEmail();

    const submitButton = klaviyoForm.querySelector(
      'button[type="submit"], input[type="submit"], button[role="button"], button',
    );

    if (submitButton) {
      submitButton.click();
    } else {
      klaviyoForm.requestSubmit();
    }
  }

  getCouponCode() {
    const couponButton = this.klaviyoMount.querySelector('button[aria-label="Copy coupon code" i]');
    const couponCode = couponButton?.querySelector("div")?.textContent.trim() || "";

    if (couponCode.toLowerCase().startsWith("loading coupon")) return "";

    return couponCode;
  }

  completeSubmission() {
    const couponCode = this.getCouponCode();

    if (!couponCode || this.section.classList.contains("submitted")) return;

    const codeTemplate = this.codeOutput.dataset.codeTemplate || this.codeOutput.textContent;

    this.codeOutput.textContent = codeTemplate.replaceAll("[[code]]", couponCode);
    this.section.classList.add("submitted");
    this.pendingSubmit = false;
    this.setLoading(false);
  }

  setLoading(isLoading) {
    this.isLoading = isLoading;
    this.visibleSubmit.disabled = isLoading;
    this.visibleSubmit.classList.toggle("is-loading", isLoading);
    this.visibleSubmit.setAttribute("aria-busy", String(isLoading));
  }
}

customElements.define("gift-insert-form", GiftInsertForm);

class CollectionPriceRange extends HTMLElement {
  constructor() {
    super();

    this.min_input = this.querySelector("[data-price-input='min']");
    this.max_input = this.querySelector("[data-price-input='max']");
    this.min_range = this.querySelector("[data-price-range-handle='min']");
    this.max_range = this.querySelector("[data-price-range-handle='max']");
    this.range_fill = this.querySelector("[data-price-range-fill]");
    this.apply_button = this.querySelector("[data-price-apply]");
    this.min = this.parseValue(this.dataset.min, 0);
    this.max = this.parseValue(this.dataset.max, 0);
    this.events = [
      {
        el: this,
        type: "input",
        callback: this.inputHandler.bind(this),
      },
      {
        el: this,
        type: "change",
        callback: this.changeHandler.bind(this),
      },
      {
        el: this.apply_button,
        type: "click",
        callback: this.applyHandler.bind(this),
      },
    ];

    this.syncHandlesWithInputs();
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }

  inputHandler(e) {
    e.stopPropagation();

    const range_handle = e.target.dataset.priceRangeHandle;
    const price_input = e.target.dataset.priceInput;

    if (range_handle) {
      this.syncInputsWithHandles(range_handle);
    } else if (price_input) {
      this.syncHandlesWithInputs(price_input);
    }
  }

  changeHandler(e) {
    e.stopPropagation();
  }

  applyHandler() {
    this.normaliseInputs();
    const filters = this.closest("main-collection-filters");

    if (!filters) return;

    filters.classList.add("filters-loading");
    filters.updateFilters();

    if (window.innerWidth > 767) {
      this.closest("details.filter")?.removeAttribute("open");
    }
  }

  syncFromParams(params) {
    this.min_input.value = params.get(this.min_input.name) || "";
    this.max_input.value = params.get(this.max_input.name) || "";
    this.syncHandlesWithInputs();
  }

  reset() {
    this.min_input.value = "";
    this.max_input.value = "";
    this.syncHandlesWithInputs();
  }

  syncInputsWithHandles(active_handle) {
    let min_value = this.parseValue(this.min_range.value, this.min);
    let max_value = this.parseValue(this.max_range.value, this.max);

    if (min_value > max_value) {
      if (active_handle === "min") {
        max_value = min_value;
      } else {
        min_value = max_value;
      }
    }

    this.min_range.value = min_value;
    this.max_range.value = max_value;
    this.min_input.value = min_value <= this.min ? "" : this.formatValue(min_value);
    this.max_input.value = max_value >= this.max ? "" : this.formatValue(max_value);
    this.updateRangeFill();
  }

  syncHandlesWithInputs(active_input) {
    let min_value = this.parseValue(this.min_input.value, this.min);
    let max_value = this.parseValue(this.max_input.value, this.max);

    min_value = this.clamp(min_value, this.min, this.max);
    max_value = this.clamp(max_value, this.min, this.max);

    if (min_value > max_value) {
      if (active_input === "min") {
        max_value = min_value;
      } else {
        min_value = max_value;
      }
    }

    this.min_range.value = min_value;
    this.max_range.value = max_value;
    this.updateRangeFill();
  }

  normaliseInputs() {
    const min_value = this.parseValue(this.min_range.value, this.min);
    const max_value = this.parseValue(this.max_range.value, this.max);

    this.min_input.value = min_value <= this.min ? "" : this.formatValue(min_value);
    this.max_input.value = max_value >= this.max ? "" : this.formatValue(max_value);
  }

  updateRangeFill() {
    const min_value = this.parseValue(this.min_range.value, this.min);
    const max_value = this.parseValue(this.max_range.value, this.max);
    const range = this.max - this.min;
    const min_position = range ? ((min_value - this.min) / range) * 100 : 0;
    const max_position = range ? ((max_value - this.min) / range) * 100 : 100;

    this.range_fill.style.left = `${min_position}%`;
    this.range_fill.style.right = `${100 - max_position}%`;
  }

  parseValue(value, fallback) {
    const parsed_value = Number.parseFloat(String(value).replace(/,/g, ""));

    return Number.isNaN(parsed_value) ? fallback : parsed_value;
  }

  formatValue(value) {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
}
customElements.define("collection-price-range", CollectionPriceRange);

class GiftOptions extends HTMLElement {
  constructor() {
    super();
    this.events = [
      { el: this, type: "input", callback: this.inputHandler.bind(this) },
      { el: document, type: "variant:changed", callback: this.changeHandler.bind(this) },
      { el: document, type: "submit", callback: this.submitHandler.bind(this), capture: true },
    ];
  }

  connectedCallback() {
    this.form = this.closest('form[action*="/cart/add"]');
    this.productTitle = this.dataset.productTitle || "";
    this.noteLimit = parseInt(this.dataset.noteLimit, 10) || 200;
    this.note = this.querySelector("[data-gift-note]");
    this.noteCount = this.querySelector("[data-gift-note-count]");
    this.wrapFlag = this.querySelector("[data-gift-wrap-flag]");
    this.wrapRef = this.querySelector("[data-gift-ref]");
    this.errorEl = this.querySelector("[data-gift-error]");
    this.events.push({ el: this.form, type: "change", callback: this.changeHandler.bind(this) });
    handleEventListeners(this.events, "add");
    this.updateCount();
    this.syncVariant(
      this.form.querySelector(
        '[name="id"]:checked, select[name="id"], input[type="hidden"][name="id"]',
      )?.value,
    );
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
    this.events = this.events.filter((event) => event.el !== this.form);
  }

  syncVariant(variantId) {
    this.hidden = String(variantId) !== this.dataset.variantId;
    this.querySelectorAll("input, textarea").forEach((input) => {
      input.disabled = this.hidden;
    });
    if (this.hidden) {
      const noWrap = this.querySelector('[data-gift-wrap-input][value=""]');
      if (noWrap) noWrap.checked = true;
    }
    this.syncHiddenFields();
    this.clearError();
  }

  /* --- State ------------------------------------------------------------- */

  get selectedWrap() {
    if (this.hidden) return null;
    const input = this.querySelector("[data-gift-wrap-input]:checked");
    if (!input || !input.value) return null;
    return { id: input.value, name: input.dataset.wrapName || "Gift wrap" };
  }

  get submitButton() {
    return this.form ? this.form.querySelector('[type="submit"]') : null;
  }

  inputHandler(e) {
    if (e.target === this.note) this.updateCount();
  }

  changeHandler(e) {
    if (e.type === "variant:changed") {
      if (e.target.closest(".shopify-section") !== this.closest(".shopify-section")) return;
      this.syncVariant(e.detail.variant?.id);
    } else if (e.target.matches('[name="id"]')) {
      this.syncVariant(e.target.value);
    } else if (e.target.matches(".gift-options__toggle")) {
      this.form.querySelectorAll(".gift-options__toggle").forEach((toggle) => {
        toggle.checked = e.target.checked;
      });
    } else if (e.target.matches("[data-gift-wrap-input]") && this.contains(e.target)) {
      this.syncHiddenFields();
      this.clearError();
    }
  }

  updateCount() {
    if (!this.note || !this.noteCount) return;
    const length = this.note.value.length;
    this.noteCount.textContent = length;
    this.noteCount.parentElement.toggleAttribute("data-limit-reached", length >= this.noteLimit);
  }

  /**
   * The hidden property inputs stay disabled (and therefore unsubmitted) until
   * a wrap is actually chosen.
   */
  syncHiddenFields() {
    const wrap = this.selectedWrap;
    if (this.wrapFlag) this.wrapFlag.disabled = !wrap;
    if (this.wrapRef) {
      this.wrapRef.disabled = !wrap;
      this.wrapRef.value = wrap ? this.generateRef() : "";
    }
  }

  /**
   * Shopify stores a property submitted with an empty value, so an untouched
   * note would appear on the order as "Gift Note:" with nothing after it.
   * Disabled fields are excluded from FormData, so the property is never
   * created. Restored immediately afterwards so the field stays usable if
   * the add fails and the customer is still on the page.
   */
  pruneEmptyNote() {
    if (!this.note || this.note.value.trim() !== "") return;
    this.note.disabled = true;
    setTimeout(() => {
      this.note.disabled = false;
    }, 0);
  }

  generateRef() {
    return `gw-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  }

  /* --- Submit ------------------------------------------------------------ */

  submitHandler(e) {
    if (e.target !== this.form || this.hidden || this.form.closest("build-a-box")) return;
    if (this.submitButton?.disabled) return;

    this.pruneEmptyNote();

    const wrap = this.selectedWrap;
    if (!wrap) return; // Use the existing product form for an unwrapped item.

    e.preventDefault();
    e.stopPropagation(); // Prevent a second add from the product form.

    // Prevent repeated submissions.
    const button = this.submitButton;
    if (button && button.getAttribute("aria-disabled") === "true") return;

    this.addToCart(wrap);
  }

  buildItems(wrap) {
    const data = new FormData(this.form);
    const variantId = data.get("id");
    const quantity = parseInt(data.get("quantity"), 10) || 1;

    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && String(value).trim() !== "") properties[match[1]] = value;
    }

    // Ensure the ref is identical on both lines even if the DOM changed.
    const ref = properties._gift_ref || this.generateRef();
    properties["Gift Wrap"] = "Yes";
    properties._gift_ref = ref;
    properties._gift_wrap_style = wrap.name;

    return [
      { id: variantId, quantity, properties },
      {
        id: wrap.id,
        quantity,
        properties: {
          "Gift wrap for": this.productTitle,
          _gift_ref: ref,
        },
      },
    ];
  }

  async addToCart(wrap) {
    this.setLoading(true);
    this.clearError();

    const addUrl =
      (typeof routes !== "undefined" && routes.cart_add_url) ||
      `${window.Shopify?.routes?.root || "/"}cart/add.js`;

    try {
      const response = await fetch(addUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ items: this.buildItems(wrap) }),
      });

      const result = await response.json();

      // Shopify returns a `status` key on the payload when the add failed.
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || "");
      }

      // Leave the button loading — the page is navigating away.
      window.location = (window.routes && window.routes.cart_url) || "/cart";
    } catch (error) {
      this.setLoading(false);
      this.showError(error.message || "We could not add this to your basket. Please try again.");
    }
  }

  /* --- Feedback ---------------------------------------------------------- */

  /** Mark the form as busy while the cart request is pending. */
  setLoading(isLoading) {
    const button = this.submitButton;
    if (!button) return;

    button.classList.toggle("loading", isLoading);
    if (isLoading) {
      button.setAttribute("aria-disabled", "true");
    } else {
      button.removeAttribute("aria-disabled");
    }

    const spinner = this.form.querySelector(".loading__spinner");
    if (spinner) spinner.classList.toggle("hidden", !isLoading);
  }

  /** Use the form error message when present, otherwise show the gift error. */
  showError(message) {
    const wrapper = this.form.querySelector(".product-form__error-message-wrapper");
    const target = wrapper && wrapper.querySelector(".product-form__error-message");

    if (wrapper && target) {
      target.textContent = message;
      wrapper.removeAttribute("hidden");
      return;
    }

    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  clearError() {
    const wrapper = this.form && this.form.querySelector(".product-form__error-message-wrapper");
    if (wrapper) wrapper.setAttribute("hidden", "");

    if (!this.errorEl) return;
    this.errorEl.textContent = "";
    this.errorEl.hidden = true;
  }
}

if (!customElements.get("gift-options")) {
  customElements.define("gift-options", GiftOptions);
}
